import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { TabState, DataValue, createDatasetId, createColumnId, createColumnName, hasData, GLMFamily, GLMLink, createModelId } from '../../../../types';
import type { GLMConfig } from '../../../../types/tabs';
import type { IncompleteObservation } from '../../../../types/model.types';
import type { GLMProgress } from '../../../utils/glm';
import { glmResultToModel } from '../../../utils/glm';
import type { GLMResult } from '../../../../types/tabs/glm.types';
import type { GLMWorkerRequest, GLMWorkerResponse } from '../../../workers/glm.worker.types';
import { ModelManager } from '../../../utils/ModelManager';
import { DatasetAccessor } from '../../../utils/DatasetAccessor';
import { ROW_ID_COLUMN_ID } from '../../../../types/system-columns';
import { useProjectData } from "../../../stores/hooks/useProjectData"
import { useTabState } from "./hooks/useTabState"
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { useConfirmDialogStore } from '../../../stores/confirmDialogStore';
import { createModelDetailTab, TabRegistry } from '../../../tab-system/TabRegistry';
import logger from '../../../utils/logger';
import { createDiagnosticDataset } from '../../../utils/glm-diagnostics';
import { ContextHelpButtonSimple } from '../../ContextHelp/ContextHelpButtonSimple';
import { GLMProgressDialog } from './GLMProgressDialog';
import * as styles from '../../../styles/components/glm-tab.css.ts';

/**
 * GLMTabContent
 *
 * GLM (一般化線形モデル) タブコンテンツ
 * 汎用的なuseTabStateを使用してpane split対応
 */

interface GLMTabContentProps {
  tab: TabState;
}

export function GLMTabContent({ tab }: GLMTabContentProps) {
  const { project, getAllDatasets, updateProject } = useProjectData();
  const { addTab, getActiveContainer, updateTabState, findTab } = usePaneLayout();
  const confirm = useConfirmDialogStore(state => state.confirm);

  // useTabStateで状態管理（pane split対応）
  const [config, setConfig] = useTabState<GLMConfig>(tab, {
    selectedDatasetId: undefined,
    selectedXColumns: [],
    selectedYColumn: undefined,
    family: 'gaussian',
    link: 'identity',
    includeIntercept: true,
    maxIterations: 100,
    tolerance: 1e-6,
    theta: 1.0,
    glmResult: undefined,
    error: undefined,
    modelSaved: false,
    isRunning: false,
    modelName: undefined,
    savedModelId: undefined
  });

  // 進捗状態（ローカルステート - タブ間で共有不要）
  const [currentProgress, setCurrentProgress] = useState<GLMProgress | null>(null);
  const [progressHistory, setProgressHistory] = useState<GLMProgress[]>([]);
  const lastProgressUpdateRef = useRef<number>(0);
  const [showProgressDialog, setShowProgressDialog] = useState<boolean>(false);

  // GLM Worker参照
  const workerRef = useRef<Worker | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent<GLMWorkerResponse>) => void) | null>(null);
  const errorHandlerRef = useRef<((event: ErrorEvent) => void) | null>(null);

  // 結果セクションへの参照（自動スクロール用）
  const resultsRef = useRef<HTMLDivElement>(null);

  // 結果が表示されたら自動的にスクロール
  useEffect(() => {
    if (config.glmResult && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [config.glmResult]);

  // 利用可能なデータセット一覧
  const datasets = useMemo(() => getAllDatasets(), [getAllDatasets]);

  // 選択されたデータセット
  const selectedDataset = useMemo(() => {
    if (!config.selectedDatasetId) return null;
    const dataset = datasets.find(ds => ds.id === config.selectedDatasetId) || null;
    return dataset;
  }, [config.selectedDatasetId, datasets]);

  // 数値型のカラムのみフィルタリング
  const numericColumns = useMemo(() => {
    if (!selectedDataset || !selectedDataset.schema?.columns) return [];
    return selectedDataset.schema.columns.filter(col =>
      col.type === 'int64' || col.type === 'float64'
    );
  }, [selectedDataset]);

  // 係数の変数名（進捗ダイアログ用）
  const variableNames = useMemo(() => {
    const names: string[] = [];
    if (config.includeIntercept) {
      names.push('Intercept');
    }
    config.selectedXColumns.forEach(colId => {
      const col = numericColumns.find(c => c.id === colId);
      names.push(col?.name || colId);
    });
    return names;
  }, [config.includeIntercept, config.selectedXColumns, numericColumns]);

  // デフォルトモデル名を生成
  const generateDefaultModelName = useCallback(() => {
    if (!config.selectedYColumn || config.selectedXColumns.length === 0) {
      return 'GLM Model';
    }

    const yName = numericColumns.find(c => c.id === config.selectedYColumn)?.name || config.selectedYColumn;
    const xNames = config.selectedXColumns
      .map(id => numericColumns.find(c => c.id === id)?.name || id)
      .join(' + ');

    const familyName = config.family.charAt(0).toUpperCase() + config.family.slice(1);
    const linkName = config.link;

    return `GLM: ${yName} ~ ${xNames} (${familyName}, ${linkName})`;
  }, [config.selectedYColumn, config.selectedXColumns, config.family, config.link, numericColumns]);

  // モデル詳細タブを開く
  const openModelDetails = useCallback(() => {
    if (!project || !config.savedModelId) return;

    const modelManager = new ModelManager(project);
    const model = modelManager.getModel(createModelId(config.savedModelId));
    if (!model) {
      logger.error('[GLMTabContent] Saved model not found:', { modelId: config.savedModelId });
      return;
    }

    const modelTab = createModelDetailTab(createModelId(config.savedModelId), model.name);
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, modelTab, { activatePane: true });
    }
  }, [project, config.savedModelId, addTab, getActiveContainer]);

  const openDiagnostics = useCallback(() => {
    if (!project || !config.savedModelId) return;

    const modelManager = new ModelManager(project);
    const model = modelManager.getModel(createModelId(config.savedModelId));
    if (!model) {
      logger.error('[GLMTabContent] Saved model not found:', { modelId: config.savedModelId });
      return;
    }

    const diagnosticsTab = TabRegistry['glm-diagnostics'].createTab(
      createModelId(config.savedModelId),
      `GLM Diagnostics: ${model.name}`
    );
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, diagnosticsTab, { activatePane: true });
    }
  }, [project, config.savedModelId, addTab, getActiveContainer]);

  // 同一設定の既存モデルを検出
  const findDuplicateModel = useCallback(() => {
    if (!project?.models || !config.selectedDatasetId || !config.selectedYColumn) return null;

    const models = Object.values(project.models);
    return models.find(model => {
      if (model.type !== 'glm') return false;

      const metadata = model.metadata as {
        datasetId?: string;
        target?: string;
        family?: string;
        link?: string;
        features?: string[];
      };
      if (metadata.datasetId !== config.selectedDatasetId) return false;
      if (metadata.target !== config.selectedYColumn) return false;
      if (metadata.family !== config.family) return false;
      if (metadata.link !== config.link) return false;

      // 特徴量の比較（順序を無視）
      const existingFeatures = new Set(metadata.features || []);
      const currentFeatures = new Set(config.selectedXColumns);

      if (existingFeatures.size !== currentFeatures.size) return false;
      for (const feature of currentFeatures) {
        if (!existingFeatures.has(feature)) return false;
      }

      return true;
    });
  }, [project, config.selectedDatasetId, config.selectedYColumn, config.selectedXColumns, config.family, config.link]);

  // ユーザー操作のハンドラー
  const handleDatasetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDatasetId = e.target.value === '' ? undefined : createDatasetId(e.target.value);
    setConfig(prev => ({ ...prev, selectedDatasetId: newDatasetId }));
  };

  const handleXColumnToggle = (columnId: string) => {
    setConfig(prev => {
      const columnIdTyped = createColumnId(columnId);
      const newColumns = prev.selectedXColumns.includes(columnIdTyped)
        ? prev.selectedXColumns.filter(id => id !== columnIdTyped)
        : [...prev.selectedXColumns, columnIdTyped];
      return { ...prev, selectedXColumns: newColumns };
    });
  };

  const handleYColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYColumn = e.target.value;
    const newYColumnId = newYColumn ? createColumnId(newYColumn) : undefined;

    setConfig(prev => {
      // Y変数が選択されたら、X変数からそれを除外
      const newXColumns = newYColumnId && prev.selectedXColumns.includes(newYColumnId)
        ? prev.selectedXColumns.filter(col => col !== newYColumnId)
        : prev.selectedXColumns;

      return { ...prev, selectedYColumn: newYColumnId, selectedXColumns: newXColumns };
    });
  };

  const handleFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFamily = e.target.value as GLMFamily;

    // ファミリーに応じてデフォルトリンク関数を設定
    const defaultLinks: Record<GLMFamily, GLMLink> = {
      gaussian: 'identity',
      binomial: 'logit',
      poisson: 'log',
      gamma: 'inverse',
      'negative-binomial': 'log'
    };

    setConfig(prev => ({ ...prev, family: newFamily, link: defaultLinks[newFamily] }));
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setConfig(prev => ({ ...prev, link: e.target.value as GLMLink }));
  };

  const handleThetaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) {
      setConfig(prev => ({ ...prev, theta: value }));
    }
  };

  // モデルを保存
  const saveModel = useCallback(async (result: GLMResult) => {
    if (!result || !project || !config.selectedDatasetId || !config.selectedYColumn) return;

    try {
      // 重複モデルをチェック
      const duplicateModel = findDuplicateModel();
      if (duplicateModel) {
        const confirmMessage = `A model with the same configuration already exists:\n\n"${duplicateModel.name}"\n\nDo you want to overwrite it?`;
        const shouldOverwrite = await confirm({
          title: 'Overwrite Existing Model',
          message: confirmMessage,
          confirmLabel: 'Overwrite',
          confirmStyle: 'danger',
          testId: 'overwrite-model-confirm'
        });

        if (!shouldOverwrite) {
          logger.info('[GLMTabContent] User canceled model save due to duplicate');
          return;
        }

        // 上書きする場合、既存モデルを削除
        logger.info('[GLMTabContent] Overwriting existing model', { modelId: duplicateModel.id });
        updateProject(proj => {
          const models = { ...proj.models };
          delete models[duplicateModel.id];
          return {
            ...proj,
            models
          };
        });
      }

      // モデル名を使用（config.modelNameまたはデフォルト名）
      const modelName = config.modelName || generateDefaultModelName();

      // 診断データセットを作成（元データセットの確認）
      if (!selectedDataset) {
        throw new Error('Dataset not found');
      }
      if (selectedDataset.type === 'ephemeral') {
        throw new Error('Cannot create diagnostics for ephemeral dataset');
      }

      // 診断データセットのIDを生成
      const diagnosticDatasetId = createDatasetId(crypto.randomUUID());

      // glmResultToModelを使用してモデルを作成
      const model = glmResultToModel(
        result,
        modelName,
        config.selectedDatasetId!,
        config.selectedXColumns,
        config.selectedYColumn!,
        config.family,
        config.link,
        config.includeIntercept,
        config.maxIterations,
        config.tolerance,
        diagnosticDatasetId
      );

      // 診断データセットを作成（DerivedDataSetの場合は自動的にデータをロード）
      const diagnosticDataset = await createDiagnosticDataset(
        selectedDataset,
        model,
        result.diagnostics
      );

      // 診断データセットのIDを上書き（createDiagnosticDatasetは新しいIDを生成するため）
      diagnosticDataset.id = diagnosticDatasetId;

      // プロジェクトを更新（モデルと診断データセットを両方追加）
      updateProject(proj => ({
        ...proj,
        models: {
          ...proj.models,
          [model.id]: model
        },
        datasets: {
          ...proj.datasets,
          [diagnosticDataset.id]: diagnosticDataset
        }
      }));
      setConfig(prev => ({ ...prev, modelSaved: true, savedModelId: model.id }));

      // タブのmetadataにsavedModelIdを追加（モデル削除時の依存関係チェック用）
      const tabLocation = findTab(tab.id);
      logger.info('[GLMTabContent] Updating tab metadata with savedModelId', {
        tabId: tab.id,
        modelId: model.id,
        hasTabLocation: !!tabLocation
      });
      if (tabLocation) {
        updateTabState(tabLocation.container.id, tab.id, (currentTab) => ({
          ...currentTab,
          metadata: {
            ...currentTab.metadata,
            savedModelId: model.id
          }
        }));
      }
      logger.info('[GLMTabContent] Model and diagnostic dataset saved successfully', {
        modelId: model.id,
        diagnosticDatasetId: diagnosticDataset.id
      });
    } catch (err) {
      logger.error('[GLMTabContent] Failed to save model:', err);
      setConfig(prev => ({ ...prev, error: 'Failed to save model' }));
    }
  }, [config, project, selectedDataset, updateProject, setConfig, findDuplicateModel, generateDefaultModelName, confirm, findTab, tab.id, updateTabState]);

  // 収束履歴をデータセットとして保存
  const saveConvergenceHistory = useCallback(() => {
    if (!project || progressHistory.length === 0) return;

    try {
      // 表示されているエントリを取得（フィルタリング済み）
      const hasThetaEstimation = progressHistory.some(p => p.isDuringThetaEstimation);
      const yName = numericColumns.find(c => c.id === config.selectedYColumn)?.name || config.selectedYColumn || 'Y';
      const familyName = config.family.charAt(0).toUpperCase() + config.family.slice(1);

      let dataset: import('../../../../types').PrimaryDataSet;
      let rowCount: number;

      if (hasThetaEstimation) {
        // ===== θ推定ありの場合 =====
        const thetaEstimationEntries = progressHistory.filter(p => p.isDuringThetaEstimation && p.isAdoptedTheta);
        const finalIRLSEntries = progressHistory.filter(p => !p.isDuringThetaEstimation && p.thetaIteration === undefined);
        const allEntries = [...thetaEstimationEntries, ...finalIRLSEntries];

        allEntries.sort((a, b) => {
          const aTheta = a.thetaIteration ?? 999;
          const bTheta = b.thetaIteration ?? 999;
          if (aTheta !== bTheta) return aTheta - bTheta;
          const aT = a.theta ?? 0;
          const bT = b.theta ?? 0;
          if (Math.abs(aT - bT) > 0.0001) return aT - bT;
          return a.iteration - b.iteration;
        });

        rowCount = allEntries.length;

        // 最後のθイテレーション番号を計算
        const maxThetaIteration = Math.max(...thetaEstimationEntries.map(e => e.thetaIteration ?? 0));
        const finalThetaIterationNumber = maxThetaIteration + 1;

        // 列を定義
        const columns: Array<{ id: string; name: string; type: 'int64' | 'float64' | 'string' }> = [
          { id: 'theta_iter', name: 'theta_iter', type: 'int64' },
          { id: 'gs', name: 'gs', type: 'string' },
          { id: 'theta', name: 'theta', type: 'float64' },
          { id: 'irls_iter', name: 'irls_iter', type: 'int64' },
          { id: 'deviance', name: 'deviance', type: 'float64' },
          { id: 'log_likelihood', name: 'log_likelihood', type: 'float64' },
        ];

        // 係数の列を追加
        variableNames.forEach(varName => {
          columns.push({
            id: `coef_${varName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`,
            name: varName,
            type: 'float64'
          });
        });

        // データを構築
        const data: Record<string, DataValue[]> = {};
        columns.forEach(col => {
          data[col.id] = [];
        });

        allEntries.forEach(entry => {
          data['theta_iter'].push(entry.thetaIteration !== undefined ? entry.thetaIteration : finalThetaIterationNumber);
          data['gs'].push(entry.goldenSectionIndicator || '-');
          data['theta'].push(entry.theta ?? null);
          data['irls_iter'].push(entry.iteration);
          data['deviance'].push(entry.deviance ?? null);
          data['log_likelihood'].push(entry.logLikelihood ?? null);

          // 係数
          variableNames.forEach((varName, i) => {
            const colId = `coef_${varName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
            data[colId].push(entry.coefficients?.[i] ?? null);
          });
        });

        // データセットを作成
        const datasetId = createDatasetId(crypto.randomUUID());
        dataset = {
          id: datasetId,
          name: `GLM Convergence (${yName}, ${familyName})`,
          type: 'primary',
          schema: {
            columns: columns.map(col => ({
              id: createColumnId(col.id),
              name: createColumnName(col.name),
              type: col.type,
              scale: {
                scale: col.type === 'string' ? 'nominal' as const : 'ratio' as const,
                inferredAutomatically: true
              },
              nullable: true
            }))
          },
          rowCount,
          data: Object.fromEntries(
            columns.map(col => [createColumnId(col.id), data[col.id]])
          ),
          metadata: {
            originalFileName: `glm_convergence_${Date.now()}.csv`,
            importedAt: new Date().toISOString(),
            description: `GLM convergence history with theta estimation for ${yName} ~ ${config.selectedXColumns.map(id => numericColumns.find(c => c.id === id)?.name || id).join(' + ')} (${familyName} family, ${config.link} link). Generated from GLM analysis on ${new Date().toLocaleDateString()}.`
          }
        };
      } else {
        // ===== θ推定なしの場合（シンプルなスキーマ） =====
        const allEntries = progressHistory.filter(p => p.stage === 'irls' && p.deviance !== undefined);
        rowCount = allEntries.length;

        // 列を定義（θ関連の列を除外）
        const columns: Array<{ id: string; name: string; type: 'int64' | 'float64' }> = [
          { id: 'irls_iter', name: 'irls_iter', type: 'int64' },
          { id: 'deviance', name: 'deviance', type: 'float64' },
        ];

        // 係数の列を追加
        variableNames.forEach(varName => {
          columns.push({
            id: `coef_${varName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`,
            name: varName,
            type: 'float64'
          });
        });

        // データを構築
        const data: Record<string, DataValue[]> = {};
        columns.forEach(col => {
          data[col.id] = [];
        });

        allEntries.forEach(entry => {
          data['irls_iter'].push(entry.iteration);
          data['deviance'].push(entry.deviance ?? null);

          // 係数
          variableNames.forEach((varName, i) => {
            const colId = `coef_${varName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
            data[colId].push(entry.coefficients?.[i] ?? null);
          });
        });

        // データセットを作成
        const datasetId = createDatasetId(crypto.randomUUID());
        dataset = {
          id: datasetId,
          name: `GLM Convergence (${yName}, ${familyName})`,
          type: 'primary',
          schema: {
            columns: columns.map(col => ({
              id: createColumnId(col.id),
              name: createColumnName(col.name),
              type: col.type,
              scale: {
                scale: 'ratio' as const,
                inferredAutomatically: true
              },
              nullable: true
            }))
          },
          rowCount,
          data: Object.fromEntries(
            columns.map(col => [createColumnId(col.id), data[col.id]])
          ),
          metadata: {
            originalFileName: `glm_convergence_${Date.now()}.csv`,
            importedAt: new Date().toISOString(),
            description: `GLM convergence history for ${yName} ~ ${config.selectedXColumns.map(id => numericColumns.find(c => c.id === id)?.name || id).join(' + ')} (${familyName} family, ${config.link} link). Generated from GLM analysis on ${new Date().toLocaleDateString()}.`
          }
        };
      }

      // プロジェクトに追加
      updateProject(proj => ({
        ...proj,
        datasets: {
          ...proj.datasets,
          [dataset.id]: dataset
        }
      }));

      logger.info('[GLMTabContent] Convergence history saved as dataset', {
        datasetId: dataset.id,
        rowCount
      });
    } catch (err) {
      logger.error('[GLMTabContent] Failed to save convergence history:', err);
    }
  }, [project, progressHistory, variableNames, config, numericColumns, updateProject]);

  // ========================================
  // Worker管理: 初期化とクリーンアップ
  // ========================================

  // Worker初期化（runGLM内で呼ばれる）
  const initializeWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker(
        new URL('../../../workers/glm.worker.ts', import.meta.url),
        { type: 'module' }
      );
      logger.info('[GLMTabContent] GLM Worker initialized');
    }
    return workerRef.current;
  }, []);

  // Workerクリーンアップ（isRunning: false時に自動実行）
  const cleanupWorker = useCallback(() => {
    if (workerRef.current) {
      // イベントリスナーを削除
      if (messageHandlerRef.current) {
        workerRef.current.removeEventListener('message', messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
      if (errorHandlerRef.current) {
        workerRef.current.removeEventListener('error', errorHandlerRef.current);
        errorHandlerRef.current = null;
      }
      // Workerを終了
      workerRef.current.terminate();
      workerRef.current = null;
      logger.info('[GLMTabContent] GLM Worker cleaned up');
    }
  }, []);

  // GLM実行が終了したら（isRunning: true → false）Workerをクリーンアップ
  useEffect(() => {
    if (!config.isRunning && workerRef.current) {
      cleanupWorker();
      logger.info('[GLMTabContent] GLM Worker cleaned up after execution ended');
    }
  }, [config.isRunning, cleanupWorker]);

  // GLMを実行
  const runGLM = useCallback(() => {

    if (!selectedDataset || config.selectedXColumns.length === 0 || !config.selectedYColumn) {
      setConfig(prev => ({ ...prev, error: 'Please select dataset, X variables, and Y variable' }));
      return;
    }

    try {
      // GLM実行開始：エラーをクリア、デフォルトモデル名を設定
      const defaultName = generateDefaultModelName();
      setConfig(prev => ({
        ...prev,
        error: undefined,
        modelName: defaultName,
        modelSaved: false,
        isRunning: true
      }));
      setCurrentProgress(null);
      setProgressHistory([]);
      setShowProgressDialog(true);

      // データセットがdataを持つことを確認
      if (!hasData(selectedDataset)) {
        setConfig(prev => ({ ...prev, error: 'Dataset does not have data loaded. Please reload the dataset.' }));
        return;
      }

      // DatasetAccessorを使用してデータを取得
      const accessor = new DatasetAccessor(selectedDataset);

      // データの抽出
      const X: number[][] = [];
      const y: number[] = [];
      const incompleteObservations: IncompleteObservation[] = [];

      const rowCount = accessor.getRowCount();

      // RowNumber列を取得
      const rowNumberData = accessor.getColumnDataById(ROW_ID_COLUMN_ID) as number[];

      const xColumns = config.selectedXColumns.map(colId => {
        try {
          return accessor.getColumnDataById(colId);
        } catch (err) {
          logger.error(`[GLMTabContent] Failed to get column data for ID ${colId}:`, err);
          throw new Error(`Column ID "${colId}" not found`);
        }
      });
      let yColumn: DataValue[];
      try {
        yColumn = accessor.getColumnDataById(config.selectedYColumn);
      } catch (err) {
        logger.error(`[GLMTabContent] Failed to get Y column data for ID ${config.selectedYColumn}:`, err);
        throw new Error(`Column ID "${config.selectedYColumn}" not found`);
      }

      for (let i = 0; i < rowCount; i++) {
        const xRow: number[] = [];
        let hasInvalid = false;
        const invalidVariables: string[] = [];
        let invalidReason: 'missing' | 'invalid' | 'infinite' | null = null;

        // X変数の値を取得
        for (let j = 0; j < xColumns.length; j++) {
          const xCol = xColumns[j];
          const val = xCol[i];

          if (val === null || val === undefined) {
            hasInvalid = true;
            invalidReason = 'missing';
            const colName = accessor.getColumnName(config.selectedXColumns[j]) || config.selectedXColumns[j];
            invalidVariables.push(colName);
            break;
          } else if (typeof val !== 'number') {
            hasInvalid = true;
            invalidReason = 'invalid';
            const colName = accessor.getColumnName(config.selectedXColumns[j]) || config.selectedXColumns[j];
            invalidVariables.push(colName);
            break;
          } else if (!isFinite(val)) {
            hasInvalid = true;
            invalidReason = 'infinite';
            const colName = accessor.getColumnName(config.selectedXColumns[j]) || config.selectedXColumns[j];
            invalidVariables.push(colName);
            break;
          }
          xRow.push(val);
        }

        // Y変数の値を取得
        const yVal = yColumn[i];
        if (yVal === null || yVal === undefined) {
          hasInvalid = true;
          if (!invalidReason) invalidReason = 'missing';
          const yColName = accessor.getColumnName(config.selectedYColumn) || config.selectedYColumn;
          invalidVariables.push(yColName);
        } else if (typeof yVal !== 'number') {
          hasInvalid = true;
          if (!invalidReason) invalidReason = 'invalid';
          const yColName = accessor.getColumnName(config.selectedYColumn) || config.selectedYColumn;
          invalidVariables.push(yColName);
        } else if (!isFinite(yVal as number)) {
          hasInvalid = true;
          if (!invalidReason) invalidReason = 'infinite';
          const yColName = accessor.getColumnName(config.selectedYColumn) || config.selectedYColumn;
          invalidVariables.push(yColName);
        }

        // 有効な行のみ追加、無効な行はincompleteObservationsに記録
        if (!hasInvalid) {
          X.push(xRow);
          y.push(yVal as number);
        } else {
          incompleteObservations.push({
            rowNumber: rowNumberData[i],
            reason: invalidReason!,
            variables: invalidVariables
          });
        }
      }

      if (X.length < config.selectedXColumns.length + 1) {
        throw new Error('Not enough valid data points for GLM');
      }

      logger.info('[GLMTabContent] Running GLM with Worker', {
        family: config.family,
        link: config.link,
        nRows: X.length,
        nIncomplete: incompleteObservations.length,
        nVars: config.selectedXColumns.length,
        includeIntercept: config.includeIntercept,
        maxIterations: config.maxIterations,
        tolerance: config.tolerance
      });

      // Workerを初期化（存在しなければ作成）
      const worker = initializeWorker();

      // Workerからのメッセージをハンドル
      const messageHandler = (event: MessageEvent<GLMWorkerResponse>) => {
        const { type, payload } = event.data;

        if (type === 'progress') {
          const progressData = payload as GLMProgress;

          // 履歴に追加（IRLSステージのみ。theta-estimationステージは除外）
          if (progressData.stage === 'irls') {
            setProgressHistory(prev => [...prev, progressData]);
          }

          // 表示用の進捗を更新（200msごとにスロットリング）
          const now = Date.now();
          if (now - lastProgressUpdateRef.current > 200) {
            setCurrentProgress(progressData);
            lastProgressUpdateRef.current = now;
          }
        } else if (type === 'success') {
          // 成功：結果を受け取る
          const result = payload as GLMResult;

          // diagnosticsにincompleteObservationsとnIncompleteを追加
          result.diagnostics.incompleteObservations = incompleteObservations;
          result.diagnostics.nIncomplete = incompleteObservations.length;

          setConfig(prev => ({ ...prev, glmResult: result, isRunning: false }));
          setCurrentProgress(null);
          logger.info('[GLMTabContent] GLM completed successfully', {
            converged: result.converged,
            iterations: result.iterations,
            deviance: result.deviance,
            nIncomplete: incompleteObservations.length
          });
        } else if (type === 'error') {
          // エラー：エラーメッセージを表示
          const error = payload as { message: string; stack?: string };
          logger.error('[GLMTabContent] GLM Worker error:', error);
          setConfig(prev => ({
            ...prev,
            error: error.message,
            glmResult: undefined,
            isRunning: false
          }));
          setCurrentProgress(null);
        }
      };

      const errorHandler = (event: ErrorEvent) => {
        logger.error('[GLMTabContent] GLM Worker unhandled error:', event);
        setConfig(prev => ({
          ...prev,
          error: `Worker error: ${event.message}`,
          glmResult: undefined,
          isRunning: false
        }));
        setCurrentProgress(null);
      };

      // ハンドラーをrefに保存
      messageHandlerRef.current = messageHandler;
      errorHandlerRef.current = errorHandler;

      // イベントリスナーを設定
      worker.addEventListener('message', messageHandler);
      worker.addEventListener('error', errorHandler);

      // Workerにメッセージを送信
      const request: GLMWorkerRequest = {
        type: 'fit-glm',
        payload: {
          X,
          y,
          family: config.family,
          link: config.link,
          options: {
            includeIntercept: config.includeIntercept,
            maxIterations: config.maxIterations,
            tolerance: config.tolerance
          },
          theta: config.family === 'negative-binomial' && config.useManualTheta ? config.theta : undefined
        }
      };
      worker.postMessage(request);
    } catch (err) {
      logger.error('[GLMTabContent] GLM error:', err);
      setConfig(prev => ({ ...prev, error: err instanceof Error ? err.message : 'Unknown error occurred', glmResult: undefined, isRunning: false }));
      setCurrentProgress(null);
    }
  }, [selectedDataset, config, setConfig, generateDefaultModelName, initializeWorker]);

  // ファミリーごとの使用可能なリンク関数
  const availableLinks: Record<GLMFamily, GLMLink[]> = {
    gaussian: ['identity', 'log'],
    binomial: ['logit', 'probit'],
    poisson: ['log', 'identity'],
    gamma: ['inverse', 'log', 'identity'],
    'negative-binomial': ['log']  // Phase 1: log only
  };

  return (
    <div className={styles.container}>
      <h2>Generalized Linear Model (GLM)</h2>

      <div className={styles.formSection}>
        {/* データセット選択 */}
        <div className={styles.formGroup}>
          <label htmlFor="glm-dataset-select">Dataset:</label>
          <select
            id="glm-dataset-select"
            value={config.selectedDatasetId ?? ''}
            onChange={handleDatasetChange}
            className={styles.select}
            aria-label="Select dataset for GLM analysis"
          >
            <option value="">Select a dataset</option>
            {datasets.map(ds => (
              <option key={ds.id} value={ds.id}>{ds.name}</option>
            ))}
          </select>
        </div>

        {/* 目的変数選択 */}
        {selectedDataset && (
          <div className={styles.formGroup}>
            <label htmlFor="glm-y-select">Dependent Variable (Y):</label>
            <select
              id="glm-y-select"
              value={config.selectedYColumn ?? ''}
              onChange={handleYColumnChange}
              className={styles.select}
              aria-label="Select dependent variable (Y) for GLM analysis"
            >
              <option value="">Select a variable</option>
              {numericColumns.map(col => (
                <option key={col.id} value={col.id}>{col.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* 説明変数選択 */}
        {selectedDataset && (
          <div className={styles.formGroup}>
            <label>Independent Variables (X):</label>
            <div className={styles.multiSelect}>
              {numericColumns
                .filter(col => col.id !== config.selectedYColumn) // Y変数として選択されたものは除外
                .map(col => (
                  <label key={col.id} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={config.selectedXColumns.includes(col.id)}
                      onChange={() => handleXColumnToggle(col.id)}
                    />
                    {col.name}
                  </label>
                ))}
            </div>
          </div>
        )}

        {/* ファミリーとリンク関数 */}
        <div className={styles.familyLinkRow}>
          <div className={styles.formGroup}>
            <label>Distribution Family:</label>
            <select
              value={config.family}
              onChange={handleFamilyChange}
              className={styles.select}
            >
              <option value="gaussian">Gaussian (Normal)</option>
              <option value="binomial">Binomial (Logistic)</option>
              <option value="poisson">Poisson (Count)</option>
              <option value="gamma">Gamma (Positive Continuous)</option>
              <option value="negative-binomial">Negative Binomial (Overdispersed Count)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Link Function:</label>
            <select
              value={config.link}
              onChange={handleLinkChange}
              className={styles.select}
            >
              {availableLinks[config.family].map(linkOption => (
                <option key={linkOption} value={linkOption}>
                  {linkOption.charAt(0).toUpperCase() + linkOption.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Negative Binomial専用: thetaパラメータ */}
          {config.family === 'negative-binomial' && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={config.useManualTheta ?? false}
                    onChange={(e) => setConfig(prev => ({ ...prev, useManualTheta: e.target.checked }))}
                  />
                  Manually specify θ (dispersion parameter)
                  <ContextHelpButtonSimple
                    ariaLabel="Dispersion parameter help - theta controls variance in Negative Binomial"
                    helpContent={
                      <div>
                        <h3>Dispersion Parameter (θ)</h3>
                        <p>
                          The dispersion parameter θ controls the variance of the Negative Binomial distribution.
                        </p>
                        <h4>Interpretation:</h4>
                        <ul>
                          <li><strong>θ → ∞</strong>: Converges to Poisson (variance = mean)</li>
                          <li><strong>θ ≈ 10-100</strong>: Moderate overdispersion</li>
                          <li><strong>θ ≈ 1-10</strong>: Strong overdispersion</li>
                          <li><strong>θ &lt; 1</strong>: Extreme overdispersion</li>
                        </ul>
                        <h4>Estimation modes:</h4>
                        <ul>
                          <li><strong>Automatic (default)</strong>: θ is estimated from data using profile likelihood</li>
                          <li><strong>Manual</strong>: Specify θ value manually for comparison or sensitivity analysis</li>
                        </ul>
                      </div>
                    }
                  />
                </label>
              </div>

              {config.useManualTheta && (
                <div className={styles.formGroup}>
                  <label htmlFor="theta-input">
                    θ value
                  </label>
                  <input
                    id="theta-input"
                    type="number"
                    value={config.theta ?? 1.0}
                    onChange={handleThetaChange}
                    min="0.1"
                    max="100"
                    step="0.1"
                    className={styles.numberInput}
                  />
                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                    Typically 0.1 to 100. Default: 1.0. Higher values reduce overdispersion.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 切片オプション */}
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={config.includeIntercept}
              onChange={(e) => setConfig(prev => ({ ...prev, includeIntercept: e.target.checked }))}
            />
            Include intercept
          </label>
        </div>

        {/* 高度なオプション */}
        <div className={styles.advancedOptions}>
          <div className={styles.formGroup}>
            <label>Max Iterations:</label>
            <input
              type="number"
              value={config.maxIterations}
              onChange={(e) => setConfig(prev => ({ ...prev, maxIterations: parseInt(e.target.value) }))}
              min="1"
              max="1000"
              className={styles.numberInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Convergence Tolerance:</label>
            <input
              type="number"
              value={config.tolerance}
              onChange={(e) => setConfig(prev => ({ ...prev, tolerance: parseFloat(e.target.value) }))}
              min="1e-10"
              max="1e-3"
              step="1e-6"
              className={styles.numberInput}
            />
          </div>
        </div>

        {/* 実行ボタン */}
        <button
          onClick={runGLM}
          disabled={!selectedDataset || config.selectedXColumns.length === 0 || !config.selectedYColumn || config.isRunning}
          className={styles.runButton}
          aria-label={config.isRunning ? 'GLM analysis in progress' : config.glmResult ? 'Run GLM analysis again' : 'Run GLM analysis'}
        >
          {config.isRunning ? 'Running...' : config.glmResult ? 'Run Again' : 'Run GLM'}
        </button>


        {/* GLM実行中の進捗ダイアログ */}
        {showProgressDialog && (
          <GLMProgressDialog
            isOpen={showProgressDialog}
            progress={currentProgress}
            progressHistory={progressHistory}
            isComplete={!config.isRunning && config.glmResult !== null}
            onClose={() => setShowProgressDialog(false)}
            onCancel={() => {
              logger.info('[GLMTabContent] GLM execution canceled by user');
              // 状態をリセット（isRunning: falseにすることでuseEffectがWorkerをクリーンアップ）
              setConfig(prev => ({ ...prev, isRunning: false, error: 'Analysis canceled by user' }));
              setShowProgressDialog(false);
            }}
            variableNames={variableNames}
            onSaveAsDataset={saveConvergenceHistory}
          />
        )}

        {config.error && <div className={styles.error}>{config.error}</div>}
      </div>

      {/* 結果表示 */}
      {config.glmResult && (
        <div ref={resultsRef} className={styles.resultsSection}>
          <h3>Analysis Complete</h3>

          {/* モデル適合度 */}
          <div className={styles.statusSection}>
            <div className={styles.statusGrid}>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Convergence:</span>
                <span className={config.glmResult.converged ? styles.statusSuccess : styles.statusPending}>
                  {config.glmResult.converged ? '✓ Converged' : `⚠ Max iterations (${config.glmResult.iterations})`}
                </span>
              </div>

              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Deviance:</span>
                <span>{config.glmResult.deviance.toFixed(4)}</span>
              </div>

              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>AIC:</span>
                <span>{config.glmResult.aic.toFixed(2)}</span>
              </div>

              {config.glmResult.theta !== undefined && (
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>
                    Dispersion Parameter (θ){config.glmResult.thetaEstimated !== undefined ? ' (estimated)' : ' (manual)'}:
                  </span>
                  <span>{config.glmResult.theta.toFixed(4)}</span>
                </div>
              )}
            </div>
          </div>

          {/* 係数表 */}
          <h4>Coefficients</h4>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Variable</th>
                <th>Coefficient</th>
              </tr>
            </thead>
            <tbody>
              {config.includeIntercept && (
                <tr>
                  <td>Intercept</td>
                  <td>{config.glmResult.coefficients[0].toFixed(6)}</td>
                </tr>
              )}
              {config.selectedXColumns.map((colId, index) => {
                const col = numericColumns.find(c => c.id === colId);
                const coeffIndex = config.includeIntercept ? index + 1 : index;
                return (
                  <tr key={colId}>
                    <td>{col?.name || colId}</td>
                    <td>{config.glmResult!.coefficients[coeffIndex].toFixed(6)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* モデル名入力と保存 */}
          <div className={styles.saveSection}>
            <div className={styles.formGroup}>
              <label htmlFor="glm-model-name">Model Name:</label>
              <input
                id="glm-model-name"
                type="text"
                value={config.modelName || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, modelName: e.target.value }))}
                placeholder="Enter model name"
                className={styles.select}
                disabled={config.modelSaved}
                aria-label="Enter a name for this GLM model"
              />
            </div>

            <button
              onClick={() => config.glmResult && saveModel(config.glmResult)}
              disabled={config.modelSaved || !config.modelName?.trim()}
              className={`${styles.runButton} ${styles.buttonSpacing}`}
              aria-label={config.modelSaved ? 'Model has been saved' : 'Save GLM model to project'}
            >
              {config.modelSaved ? '✓ Model Saved' : 'Save Model'}
            </button>

            {/* 保存後のリンク */}
            {config.modelSaved && config.savedModelId && (
              <div className={styles.linkSection}>
                <button
                  onClick={openModelDetails}
                  className={styles.infoButton}
                  aria-label="View detailed information about the saved model"
                >
                  📊 View Model Details
                </button>
                <button
                  onClick={openDiagnostics}
                  className={styles.infoButton}
                  aria-label="View diagnostic plots for the saved model"
                >
                  📈 View Diagnostics
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
